import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { ToastProvider } from "./components/ui/Toast";
import AppShell from "./components/layout/AppShell";

// ─── Lazy loaded pages ───────────────────────────────────────────────

// Core
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

// Orders
const OrdersList = lazy(() => import("./pages/orders/OrdersList"));
const PlacedOrders = lazy(() => import("./pages/orders/PlacedOrders"));
const AcceptedOrders = lazy(() => import("./pages/orders/AcceptedOrders"));
const OrderDetail = lazy(() => import("./pages/orders/OrderDetail"));
const OrderPrint = lazy(() => import("./pages/orders/OrderPrint"));

// Products
const ProductsList = lazy(() => import("./pages/products/ProductsList"));
const ProductCreate = lazy(() => import("./pages/products/ProductCreate"));
const ProductEdit = lazy(() => import("./pages/products/ProductEdit"));

// Categories
const CategoriesList = lazy(() => import("./pages/categories/CategoriesList"));
const CategoryCreate = lazy(() => import("./pages/categories/CategoryCreate"));
const CategoryEdit = lazy(() => import("./pages/categories/CategoryEdit"));

// Coupons
const CouponsList = lazy(() => import("./pages/coupons/CouponsList"));
// Phase 7.5.4 — CouponCreate/CouponEdit deprecated. Files retained for rollback.

// Reviews
const ReviewsList = lazy(() => import("./pages/reviews/ReviewsList"));

// Payments
const Earnings = lazy(() => import("./pages/payments/Earnings"));
const PayoutsList = lazy(() => import("./pages/payments/PayoutsList"));
// Phase 7.5.4 — PayoutRequest deprecated. File retained for rollback.

// Settings
const StoreSettings = lazy(() => import("./pages/settings/StoreSettings"));
const ProfileSettings = lazy(() => import("./pages/settings/ProfileSettings"));
const DeliverySettings = lazy(() => import("./pages/settings/DeliverySettings"));

// Dine-In
const DineIn = lazy(() => import("./pages/DineIn"));

// Special Offers
const SpecialOffers = lazy(() => import("./pages/SpecialOffers"));

// Drivers
const DriversList = lazy(() => import("./pages/drivers/DriversList"));
const DeliveryTeam = lazy(() => import("./pages/delivery/DeliveryTeam"));
const CatalogueUpload = lazy(() => import("./pages/catalogue/CatalogueUpload"));
const NewItemRequests = lazy(() => import("./pages/catalogue/NewItemRequests"));

// ─── Helper components ───────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="relative mx-auto w-12 h-12">
          <div className="absolute inset-0 border-3 border-amigo-orange/10 rounded-full" />
          <div
            className="absolute inset-0 border-3 border-transparent border-t-amigo-orange rounded-full animate-spin"
          />
          <div
            className="absolute inset-2 border-2 border-transparent border-b-amigo-teal rounded-full animate-spin"
            style={{ animationDirection: "reverse", animationDuration: "0.8s" }}
          />
        </div>
        <div className="flex items-center gap-1 justify-center">
          <div
            className="w-1.5 h-1.5 bg-amigo-orange rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          />
          <div
            className="w-1.5 h-1.5 bg-amigo-orange/70 rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          />
          <div
            className="w-1.5 h-1.5 bg-amigo-orange/40 rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          />
        </div>
      </div>
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex flex-col items-center justify-center">
      <div className="text-center space-y-6">
        <h1 className="text-4xl font-black tracking-tight">
          <span className="text-amigo-orange">ami</span>
          <span className="text-amigo-teal">Go</span>
        </h1>
        <p className="text-white/30 text-xs uppercase tracking-widest">
          Store Dashboard
        </p>
        <div className="relative mx-auto w-14 h-14">
          <div className="absolute inset-0 border-3 border-white/10 rounded-full" />
          <div
            className="absolute inset-0 border-3 border-transparent border-t-amigo-orange rounded-full animate-spin"
          />
          <div
            className="absolute inset-2.5 border-2 border-transparent border-b-amigo-teal rounded-full animate-spin"
            style={{ animationDirection: "reverse", animationDuration: "0.8s" }}
          />
        </div>
        <p className="text-white/40 text-sm tracking-wide">Loading store...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullScreenLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullScreenLoader />;

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
      <h1 className="text-6xl font-bold text-gray-200 dark:text-gray-700">
        404
      </h1>
      <p className="text-lg font-medium text-gray-600 dark:text-gray-400 mt-4">
        Page not found
      </p>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
        The page you are looking for does not exist.
      </p>
      <a
        href="/"
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-amigo-orange text-white rounded-lg font-medium text-sm hover:bg-amigo-orange-dark transition-colors"
      >
        Back to Dashboard
      </a>
    </div>
  );
}

// ─── Routes ──────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected routes inside AppShell */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route path="/" element={<Dashboard />} />

          {/* ── Orders ── */}
          <Route path="/orders" element={<OrdersList />} />
          <Route path="/orders/placed" element={<PlacedOrders />} />
          <Route path="/orders/accepted" element={<AcceptedOrders />} />
          <Route path="/orders/:id/print" element={<OrderPrint />} />
          <Route path="/orders/:id" element={<OrderDetail />} />

          {/* ── Products ── */}
          <Route path="/products" element={<ProductsList />} />
          <Route path="/products/create" element={<ProductCreate />} />
          <Route path="/products/:id/edit" element={<ProductEdit />} />
              <Route path="/catalogue" element={<CatalogueUpload />} />
              <Route path="/catalogue/new-items" element={<NewItemRequests />} />

          {/* ── Categories ── */}
          <Route path="/categories" element={<CategoriesList />} />
          <Route path="/categories/create" element={<CategoryCreate />} />
          <Route path="/categories/:id/edit" element={<CategoryEdit />} />

          {/* ── Coupons ── */}
          <Route path="/coupons" element={<CouponsList />} />
          {/* Phase 7.5.4 — deprecated. Stores don't create coupons. Redirect to read-only list. */}
          <Route path="/coupons/create" element={<Navigate to="/coupons" replace />} />
          <Route path="/coupons/:id/edit" element={<Navigate to="/coupons" replace />} />

          {/* ── Special Offers ── */}
          <Route path="/special-offers" element={<SpecialOffers />} />

          {/* ── Reviews ── */}
          <Route path="/reviews" element={<ReviewsList />} />

          {/* ── Payments ── */}
          <Route path="/payments/earnings" element={<Earnings />} />
          <Route path="/payments/payouts" element={<PayoutsList />} />
          {/* Phase 7.5.4 — deprecated. Stores don't create payout requests; admin runs settlement. */}
          <Route path="/payments/request" element={<Navigate to="/payments/payouts" replace />} />

          {/* ── Settings ── */}
          <Route path="/settings/store" element={<StoreSettings />} />
          <Route path="/settings/profile" element={<ProfileSettings />} />
          <Route path="/delivery-settings" element={<DeliverySettings />} />

          {/* ── Dine-In ── */}
          <Route path="/dinein" element={<DineIn />} />

          {/* ── Drivers ── */}
          <Route path="/drivers" element={<DriversList />} />

          {/* Store-own Delivery Team */}
          <Route path="/delivery-team" element={<DeliveryTeam />} />

          {/* 404 inside shell */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* 404 outside shell */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
